/**
 * Arena browser ↔ world-server protocol.
 *
 * Portal and Animus domain records are intentionally absent. World messages
 * may carry opaque `domainRef` values in future projection entities, but this
 * protocol only owns world membership and ephemeral spatial presence.
 */
export const WORLD_PROTOCOL = 'arena.world.v1' as const
export const WORLD_TICKET_SUBPROTOCOL_PREFIX = 'arena.world-ticket.v1.' as const

export type WorldProtocolVersion = typeof WORLD_PROTOCOL
export type PresenceAnimation = 'idle' | 'walking' | 'interacting'

export interface WorldPose {
  x: number
  z: number
  yaw: number
  animation: PresenceAnimation
}

export interface WorldMember {
  /** Stable, server-derived public id. Never supplied by the browser. */
  playerId: string
  displayName: string
  avatarId: string
  worldId: string
  roomId: string
  pose: WorldPose
  joinedAt: string
  lastSeenAt: string
}

interface ClientMessageBase {
  protocol: WorldProtocolVersion
  messageId: string
}

export interface WorldJoinMessage extends ClientMessageBase {
  type: 'world.join'
  worldId: string
  roomId: string
  resumeFrom?: number
}

export interface PresenceUpdateMessage extends ClientMessageBase {
  type: 'presence.update'
  sequence: number
  pose: WorldPose
}

export interface RoomChangeMessage extends ClientMessageBase {
  type: 'room.change'
  sequence: number
  roomId: string
}

export interface HeartbeatMessage extends ClientMessageBase {
  type: 'heartbeat'
  sequence: number
}

export type WorldClientMessage =
  | WorldJoinMessage
  | PresenceUpdateMessage
  | RoomChangeMessage
  | HeartbeatMessage

interface ServerMessageBase {
  protocol: WorldProtocolVersion
  eventId: number
  serverTime: string
}

export interface WorldSnapshotMessage extends ServerMessageBase {
  type: 'world.snapshot'
  connectionId: string
  selfPlayerId: string
  worldId: string
  roomId: string
  revision: number
  /** Snapshot is authoritative and replaces the browser's current roster. */
  members: WorldMember[]
  /** v1 reconnect always replaces local state; it never claims partial replay. */
  recovery: 'snapshot'
}

export interface PresenceUpsertMessage extends ServerMessageBase {
  type: 'presence.upsert'
  revision: number
  member: WorldMember
}

export interface PresenceRemoveMessage extends ServerMessageBase {
  type: 'presence.remove'
  revision: number
  playerId: string
  worldId: string
  roomId: string
  reason: 'left' | 'expired' | 'replaced' | 'room_change'
}

export interface HeartbeatAckMessage extends ServerMessageBase {
  type: 'heartbeat.ack'
  sequence: number
}

export interface WorldErrorMessage extends ServerMessageBase {
  type: 'world.error'
  code:
    | 'protocol_mismatch'
    | 'invalid_message'
    | 'not_joined'
    | 'forbidden_world'
    | 'forbidden_room'
    | 'rate_limited'
    | 'stale_sequence'
    | 'resync_required'
  message: string
  recoverable: boolean
}

export type WorldServerMessage =
  | WorldSnapshotMessage
  | PresenceUpsertMessage
  | PresenceRemoveMessage
  | HeartbeatAckMessage
  | WorldErrorMessage

export interface ParseResult<T> {
  ok: boolean
  value?: T
  error?: string
}

const MESSAGE_ID = /^[A-Za-z0-9._:-]{1,128}$/
const WORLD_OR_ROOM_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/
const ANIMATIONS = new Set<PresenceAnimation>(['idle', 'walking', 'interacting'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function validId(value: unknown, pattern: RegExp): value is string {
  return typeof value === 'string' && pattern.test(value)
}

function validSequence(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const allowed = new Set(keys)
  return Object.keys(value).every((key) => allowed.has(key))
}

function validTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value))
}

function parsePose(value: unknown): WorldPose | null {
  if (!isRecord(value)) return null
  const { x, z, yaw, animation } = value
  if (
    typeof x !== 'number' ||
    typeof z !== 'number' ||
    typeof yaw !== 'number' ||
    !Number.isFinite(x) ||
    !Number.isFinite(z) ||
    !Number.isFinite(yaw) ||
    Math.abs(x) > 10_000 ||
    Math.abs(z) > 10_000 ||
    Math.abs(yaw) > Math.PI * 4 ||
    typeof animation !== 'string' ||
    !ANIMATIONS.has(animation as PresenceAnimation)
  ) return null
  return { x, z, yaw, animation: animation as PresenceAnimation }
}

/** Strict decoder. Unknown message types/versions fail closed. */
export function parseWorldClientMessage(input: unknown): ParseResult<WorldClientMessage> {
  if (!isRecord(input)) return { ok: false, error: 'message must be an object' }
  if (input.protocol !== WORLD_PROTOCOL) {
    return { ok: false, error: `expected protocol ${WORLD_PROTOCOL}` }
  }
  if (!validId(input.messageId, MESSAGE_ID)) {
    return { ok: false, error: 'invalid messageId' }
  }

  const base = { protocol: WORLD_PROTOCOL, messageId: input.messageId }
  switch (input.type) {
    case 'world.join': {
      if (
        !exactKeys(input, ['protocol', 'type', 'messageId', 'worldId', 'roomId', 'resumeFrom']) ||
        !validId(input.worldId, WORLD_OR_ROOM_ID) ||
        !validId(input.roomId, WORLD_OR_ROOM_ID) ||
        (input.resumeFrom !== undefined && !validSequence(input.resumeFrom))
      ) return { ok: false, error: 'invalid world.join payload' }
      return {
        ok: true,
        value: {
          ...base,
          type: 'world.join',
          worldId: input.worldId,
          roomId: input.roomId,
          ...(input.resumeFrom === undefined ? {} : { resumeFrom: input.resumeFrom }),
        },
      }
    }
    case 'presence.update': {
      const pose = parsePose(input.pose)
      if (
        !exactKeys(input, ['protocol', 'type', 'messageId', 'sequence', 'pose']) ||
        !validSequence(input.sequence) ||
        !pose
      ) {
        return { ok: false, error: 'invalid presence.update payload' }
      }
      return {
        ok: true,
        value: { ...base, type: 'presence.update', sequence: input.sequence, pose },
      }
    }
    case 'room.change':
      if (
        !exactKeys(input, ['protocol', 'type', 'messageId', 'sequence', 'roomId']) ||
        !validSequence(input.sequence) ||
        !validId(input.roomId, WORLD_OR_ROOM_ID)
      ) {
        return { ok: false, error: 'invalid room.change payload' }
      }
      return {
        ok: true,
        value: {
          ...base,
          type: 'room.change',
          sequence: input.sequence,
          roomId: input.roomId,
        },
      }
    case 'heartbeat':
      if (
        !exactKeys(input, ['protocol', 'type', 'messageId', 'sequence']) ||
        !validSequence(input.sequence)
      ) {
        return { ok: false, error: 'invalid heartbeat payload' }
      }
      return {
        ok: true,
        value: { ...base, type: 'heartbeat', sequence: input.sequence },
      }
    default:
      return { ok: false, error: 'unknown message type' }
  }
}

/** Strict decoder shared by browser snapshots and internal Arena fanout. */
export function parseWorldMember(value: unknown): WorldMember | null {
  if (
    !isRecord(value) ||
    !exactKeys(value, [
      'playerId',
      'displayName',
      'avatarId',
      'worldId',
      'roomId',
      'pose',
      'joinedAt',
      'lastSeenAt',
    ]) ||
    !validId(value.playerId, WORLD_OR_ROOM_ID) ||
    typeof value.displayName !== 'string' ||
    value.displayName.length > 200 ||
    !validId(value.avatarId, WORLD_OR_ROOM_ID) ||
    !validId(value.worldId, WORLD_OR_ROOM_ID) ||
    !validId(value.roomId, WORLD_OR_ROOM_ID) ||
    !validTimestamp(value.joinedAt) ||
    !validTimestamp(value.lastSeenAt)
  ) return null
  const pose = parsePose(value.pose)
  if (!pose) return null
  return {
    playerId: value.playerId,
    displayName: value.displayName,
    avatarId: value.avatarId,
    worldId: value.worldId,
    roomId: value.roomId,
    pose,
    joinedAt: value.joinedAt,
    lastSeenAt: value.lastSeenAt,
  }
}

const ERROR_CODES = new Set<WorldErrorMessage['code']>([
  'protocol_mismatch',
  'invalid_message',
  'not_joined',
  'forbidden_world',
  'forbidden_room',
  'rate_limited',
  'stale_sequence',
  'resync_required',
])

/** Strict browser-side decoder. Same-version malformed data fails closed. */
export function parseWorldServerMessage(input: unknown): ParseResult<WorldServerMessage> {
  if (!isRecord(input)) return { ok: false, error: 'message must be an object' }
  if (input.protocol !== WORLD_PROTOCOL) {
    return { ok: false, error: `expected protocol ${WORLD_PROTOCOL}` }
  }
  if (
    !validSequence(input.eventId) ||
    input.eventId === 0 ||
    !validTimestamp(input.serverTime)
  ) return { ok: false, error: 'invalid server message envelope' }
  const base = {
    protocol: WORLD_PROTOCOL,
    eventId: input.eventId,
    serverTime: input.serverTime,
  }

  switch (input.type) {
    case 'world.snapshot': {
      if (
        !exactKeys(input, [
          'protocol',
          'type',
          'eventId',
          'serverTime',
          'connectionId',
          'selfPlayerId',
          'worldId',
          'roomId',
          'revision',
          'recovery',
          'members',
        ]) ||
        !validId(input.connectionId, WORLD_OR_ROOM_ID) ||
        !validId(input.selfPlayerId, WORLD_OR_ROOM_ID) ||
        !validId(input.worldId, WORLD_OR_ROOM_ID) ||
        !validId(input.roomId, WORLD_OR_ROOM_ID) ||
        !validSequence(input.revision) ||
        input.recovery !== 'snapshot' ||
        !Array.isArray(input.members) ||
        input.members.length > 10_000
      ) return { ok: false, error: 'invalid world.snapshot payload' }
      const members = input.members.map(parseWorldMember)
      if (members.some((member) => member === null)) {
        return { ok: false, error: 'invalid world.snapshot member' }
      }
      return {
        ok: true,
        value: {
          ...base,
          type: 'world.snapshot',
          connectionId: input.connectionId,
          selfPlayerId: input.selfPlayerId,
          worldId: input.worldId,
          roomId: input.roomId,
          revision: input.revision,
          recovery: 'snapshot',
          members: members as WorldMember[],
        },
      }
    }
    case 'presence.upsert': {
      const member = parseWorldMember(input.member)
      if (
        !exactKeys(input, [
          'protocol',
          'type',
          'eventId',
          'serverTime',
          'revision',
          'member',
        ]) ||
        !validSequence(input.revision) ||
        !member
      ) return { ok: false, error: 'invalid presence.upsert payload' }
      return {
        ok: true,
        value: { ...base, type: 'presence.upsert', revision: input.revision, member },
      }
    }
    case 'presence.remove':
      if (
        !exactKeys(input, [
          'protocol',
          'type',
          'eventId',
          'serverTime',
          'revision',
          'playerId',
          'worldId',
          'roomId',
          'reason',
        ]) ||
        !validSequence(input.revision) ||
        !validId(input.playerId, WORLD_OR_ROOM_ID) ||
        !validId(input.worldId, WORLD_OR_ROOM_ID) ||
        !validId(input.roomId, WORLD_OR_ROOM_ID) ||
        !['left', 'expired', 'replaced', 'room_change'].includes(String(input.reason))
      ) return { ok: false, error: 'invalid presence.remove payload' }
      return {
        ok: true,
        value: {
          ...base,
          type: 'presence.remove',
          revision: input.revision,
          playerId: input.playerId,
          worldId: input.worldId,
          roomId: input.roomId,
          reason: input.reason as PresenceRemoveMessage['reason'],
        },
      }
    case 'heartbeat.ack':
      if (
        !exactKeys(input, [
          'protocol',
          'type',
          'eventId',
          'serverTime',
          'sequence',
        ]) ||
        !validSequence(input.sequence)
      ) return { ok: false, error: 'invalid heartbeat.ack payload' }
      return {
        ok: true,
        value: { ...base, type: 'heartbeat.ack', sequence: input.sequence },
      }
    case 'world.error':
      if (
        !exactKeys(input, [
          'protocol',
          'type',
          'eventId',
          'serverTime',
          'code',
          'message',
          'recoverable',
        ]) ||
        typeof input.code !== 'string' ||
        !ERROR_CODES.has(input.code as WorldErrorMessage['code']) ||
        typeof input.message !== 'string' ||
        typeof input.recoverable !== 'boolean'
      ) return { ok: false, error: 'invalid world.error payload' }
      return {
        ok: true,
        value: {
          ...base,
          type: 'world.error',
          code: input.code as WorldErrorMessage['code'],
          message: input.message,
          recoverable: input.recoverable,
        },
      }
    default:
      return { ok: false, error: 'unknown server message type' }
  }
}

export function encodeWorldMessage(message: WorldClientMessage | WorldServerMessage): string {
  return JSON.stringify(message)
}
